import React, { useEffect, useState } from 'react';
import { makeStyles, Divider, Theme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import BasicProfileHolder from '../../components/BasicProfileHolder';
import { StyledText } from '../../components/StyledText';
import Button from '../../components/Button';
import { ConnectorReferralData, InvestorReferralResponse, ReferralStatus } from '../../types/types';
import { API } from '../../api';
import ReferralResponseModal from '../investor/ReferralResponseModal';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#FFFFFF',
    border: '1px solid #DAD9DF',
    borderRadius: '8px',
    padding: '35px 55px'
  },
  container: {
    display: 'flex',
    flexDirection: 'row'
  },

  leftSideContent: {
    width: '250px',
    display: 'flex',
    height: 'auto',
    alignItems: 'center',
    marginRight: '15px'
  },

  spacer: {
    width: '60px',
    maxWidth: '10%'
  },

  divider: {
    color: '#DAD9DF',
    marginTop: '17px',
    marginBottom: '18px'
  },

  lightButton: {
    marginRight: '17px'
  },

  alertSuccess: {
    fontWeight: 500,
    fontSize: '20px',
    color: theme.palette.success.main
  },

  alertFailure: {
    fontWeight: 500,
    fontSize: '20px',
    color: theme.palette.error.main
  }
}));

interface AlertHodlerProps {
  alert: Alert
}
const AlertHolder:React.FC<AlertHodlerProps> = ({ alert }) => {
  const { message, success } = alert;
  const styles = useStyles();
  return (
    <Box className={styles.root}>
      <StyledText variant='h3' className={success ? styles.alertSuccess : styles.alertFailure}>
        { message }
      </StyledText>
    </Box>
  );
};


interface Alert {
  message: string
  success: boolean
}
interface ReferralWidgetParams {
  referralData: ConnectorReferralData,
  referralId: string,
}

const ReferralWidget: React.FC<ReferralWidgetParams> = ({ referralData, referralId }) => {
  const styles = useStyles();
  const [pendingResponse, setPendingResponse] = useState<InvestorReferralResponse>();
  const [alert, setAlert] = useState<Alert>();

  const { connector, intro, status } = referralData;

  const handleRespondClick = async (resp: InvestorReferralResponse) => {
    setPendingResponse(resp);
  };

  useEffect(() => {
    if (status === ReferralStatus.Accepted) {
      setAlert({ success: true, message: `Looks like you've already expressed interest in this referral to ${connector.name}.` });
    } else if (status === ReferralStatus.Declined) {
      setAlert({
        success: false,
        message: `Looks like you've already delcined this referral. Please reach out to ${connector.name} directly if you'd like to reconsider.`
      });
    }
  }, [status, connector.name]);

  const handleSubmitResponse = async (message: string, resp: InvestorReferralResponse) => {
    try {
      const input = { message, referralId, referralResponse: resp };
      const response = await API.sendReferralReply(input);
      // to-do: implement ui for non-pending referrals
      setAlert({ message: `Success! Your message was sent to ${connector.name}.`, success: true });
    } catch (e: unknown) {
      setAlert({ message: `Whoops! Something went wrong and your response could not be sent. Please reach out to ${connector.name} directly.`, success: false });
      console.error('error sending referral response', e);
    } finally {
      setPendingResponse(undefined);
    }
  };

  if (alert) {
    return <AlertHolder alert={alert} />;
  }

  return (
    <Box className={styles.root}>
      <ReferralResponseModal
        response={pendingResponse}
        connector={connector}
        onClose={() => { setPendingResponse(undefined); }}
        handleSubmit={handleSubmitResponse}
      />
      <Box className={styles.container}>
        <div className={styles.leftSideContent}>
          <BasicProfileHolder profile={connector} />
        </div>
        <StyledText style={{ maxWidth: '1010px' }}>{intro}</StyledText>
      </Box>
      <Divider className={styles.divider} />
      <Box className={styles.container}>
        <div className={styles.leftSideContent}>
          <StyledText variant='h3' className={styles.leftSideContent}>Should I introduce you?</StyledText>
        </div>
        <Button className={styles.lightButton} color='secondary' onClick={() => { handleRespondClick(InvestorReferralResponse.Decline); }}>No, I pass</Button>
        <Button onClick={() => { handleRespondClick(InvestorReferralResponse.Accept); }}>Yes, refer me</Button>
      </Box>
    </Box>
  );
};

export default ReferralWidget;
